import React from 'react'
import { Link } from 'react-router-dom'
import { useCart } from '../state/CartContext'
import { createCheckoutSession } from '../api/caApi'
import { useI18n } from '../state/I18nContext'

const Checkout: React.FC = () => {
  const { state, total } = useCart()
  const { t, locale } = useI18n()
  const [buyerName, setBuyerName] = React.useState('')
  const [buyerEmail, setBuyerEmail] = React.useState('')
  const [addressLine1, setAddressLine1] = React.useState('')
  const [addressLine2, setAddressLine2] = React.useState('')
  const [postalCode, setPostalCode] = React.useState('')
  const [submitting, setSubmitting] = React.useState(false)
  const [error, setError] = React.useState<string | null>(null)

  const storageKey = 'ca_checkout_details'

  React.useEffect(() => {
    try {
      const raw = window.localStorage.getItem(storageKey)
      if (!raw) return
      const parsed = JSON.parse(raw) as {
        name?: string
        email?: string
        addressLine1?: string
        addressLine2?: string
        postalCode?: string
      } | null
      if (!parsed) return
      if (typeof parsed.name === 'string') setBuyerName(parsed.name)
      if (typeof parsed.email === 'string') setBuyerEmail(parsed.email)
      if (typeof parsed.addressLine1 === 'string') setAddressLine1(parsed.addressLine1)
      if (typeof parsed.addressLine2 === 'string') setAddressLine2(parsed.addressLine2)
      if (typeof parsed.postalCode === 'string') setPostalCode(parsed.postalCode)
    } catch {
      // ignore storage errors
    }
  }, [])

  React.useEffect(() => {
    try {
      window.localStorage.setItem(
        storageKey,
        JSON.stringify({
          name: buyerName,
          email: buyerEmail,
          addressLine1,
          addressLine2,
          postalCode,
        })
      )
    } catch {
      // ignore storage errors
    }
  }, [buyerName, buyerEmail, addressLine1, addressLine2, postalCode])

  const handleCheckout = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    if (state.items.length === 0) {
      alert(t.checkout.emptyCartAlert)
      return
    }

    if (!buyerName.trim() || !buyerEmail.trim() || !addressLine1.trim() || !postalCode.trim()) {
      setError(t.checkout.requiredFields)
      return
    }

    try {
      setSubmitting(true)
      setError(null)
      const session = await createCheckoutSession({
        items: state.items.map(i => ({ product_slug: i.id, qty: i.qty })),
        buyer_email: buyerEmail.trim() || undefined,
      }, { lang: locale })
      window.location.href = session.url
    } catch (err: any) {
      const code = String(err?.code || 'request_failed') as keyof typeof t.apiErrors
      const mapped = (t.apiErrors as any)[code] as string | undefined
      setError(mapped || err?.message || t.checkout.failed)
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <section className="max-w-6xl mx-auto px-4 py-12">
      <h1 className="text-3xl font-bold">{t.checkout.title}</h1>
      {state.items.length === 0 ? (
        <div className="mt-6 rounded-xl border border-neutral-200 p-6 bg-white">
          <p className="text-neutral-700">{t.checkout.emptyCartAlert}</p>
          <Link
            to={`/${locale}/taste`}
            className="inline-block mt-4 rounded-lg bg-amber-600 text-white px-5 py-3 font-semibold hover:bg-amber-700"
          >
            {t.cartPage.browseCatalog}
          </Link>
        </div>
      ) : null}

      {state.items.length > 0 ? (
        <div className="mt-8 grid md:grid-cols-3 gap-6">
          <form className="md:col-span-2 space-y-4" onSubmit={handleCheckout}>
            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-1">{t.checkout.fullName}</label>
              <input
                name="fullName"
                className="w-full rounded-lg border border-neutral-300 px-3 py-2"
                placeholder={t.checkout.fullName}
                type="text"
                value={buyerName}
                required
                onChange={e => setBuyerName(e.currentTarget.value)}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-1">{t.checkout.email}</label>
              <input
                name="email"
                className="w-full rounded-lg border border-neutral-300 px-3 py-2"
                placeholder={t.checkout.email}
                type="email"
                value={buyerEmail}
                required
                onChange={e => setBuyerEmail(e.currentTarget.value)}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-1">{t.checkout.addressLine1}</label>
              <input
                name="addressLine1"
                className="w-full rounded-lg border border-neutral-300 px-3 py-2"
                placeholder={t.checkout.addressLine1}
                type="text"
                value={addressLine1}
                required
                onChange={e => setAddressLine1(e.currentTarget.value)}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-1">{t.checkout.addressLine2}</label>
              <input
                name="addressLine2"
                className="w-full rounded-lg border border-neutral-300 px-3 py-2"
                placeholder={t.checkout.addressLine2}
                type="text"
                value={addressLine2}
                onChange={e => setAddressLine2(e.currentTarget.value)}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-1">{t.checkout.postalCode}</label>
              <input
                name="postalCode"
                className="w-full rounded-lg border border-neutral-300 px-3 py-2"
                placeholder={t.checkout.postalCode}
                type="text"
                value={postalCode}
                required
                onChange={e => setPostalCode(e.currentTarget.value)}
              />
            </div>

            {error ? <div className="text-sm text-red-700">{error}</div> : null}

            <button
              type="submit"
              disabled={submitting}
              className="rounded-lg bg-amber-600 text-white px-5 py-3 font-semibold hover:bg-amber-700 disabled:opacity-60"
            >
              {submitting ? t.checkout.placingOrder : t.checkout.placeOrder}
            </button>
          </form>
          <div className="rounded-xl border border-neutral-200 p-6 bg-white">
            <h2 className="font-semibold">{t.cartPage.orderSummary}</h2>
            <ul className="mt-3 space-y-2 text-sm text-neutral-700">
              {state.items.map(i => (
                <li key={i.id} className="flex items-center justify-between">
                  <span>{i.name} × {i.qty}</span>
                  <span>€ {(i.price * i.qty).toFixed(2)}</span>
                </li>
              ))}
            </ul>
            <div className="mt-4 flex items-center justify-between font-bold">
              <span>{t.cartPage.total}</span>
              <span className="text-amber-700">€ {total.toFixed(2)}</span>
            </div>
          </div>
        </div>
      ) : null}
    </section>
  )
}

export default Checkout
